import { useEffect, useState } from "react";

import {
  ReportDetails,
  ReportListRequest,
} from "../../../services/Endpoints/report/ReportListEndpoint";

import DonutChart from "../../common/PieChart";
import { useCanAccess } from "../../../hooks/canAccess";
import errorResolver from "../../../utils/error-resolver";
import { AccessEnums } from "../../../constants/accessEnum";
import MuiLinearProgress from "../common/MuiLinearProgress";
import ReloadButton from "../../common/reload-button/ReloadButton";
import { formatTimestampWithTime } from "../../../utils/date-formatter";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { useNotificationContext } from "../../../context/notificationContext";

import { LoadingButton } from "@mui/lab";
import { Card, Stack, Typography, Box } from "@mui/material";
import { Call, Clock, CloseCircle, TickCircle } from "iconsax-react";

interface VoiceBroadcastReportProps {
  forceReload?: boolean;
  refreshInterval?: number;
}

export default function VoiceBroadcastReport({
  forceReload,
  refreshInterval = 0,
}: VoiceBroadcastReportProps) {
  const [waiting, setWaiting] = useState(false);
  const [resetWaiting, setResetWaiting] = useState(false);
  const [report, setReport] = useState<ReportDetails | null>(null);

  const { can } = useCanAccess();
  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();

  const getData = async () => {
    try {
      setWaiting(true);
      const ReportList = new ReportListRequest(protectedInstance);
      const resp = await ReportList.voicebroadcastStatus();
      setReport(resp);
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setWaiting(false);
    }
  };

  const resetCallStatus = async () => {
    try {
      setResetWaiting(true);
      const ReportList = new ReportListRequest(protectedInstance);
      const resp = await ReportList.resetVoicebrodcastStatus();
      setReport(resp);
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setResetWaiting(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (forceReload) getData();
  }, [forceReload]);

  const getCardColor = () => {
    if (!report) return "#ecf0f1";
    if (report.data.pickup === 0) return "#ecf0f1";

    const failedPercent = (report.data.failed / report.data.pickup) * 100;
    if (failedPercent < 10) return "#E9F7EF";
    if (failedPercent < 40) return "#FEF9E7";
    return "#FDEDEC";
  };

  const getChartData = (report: ReportDetails) => {
    if (!report || !report.data.status) return [];

    const s = report.data.status;
    
    const labels = {
      busy: "مشغول",
      failed: "ناموفق",
      rejected: "رد شده",
      canceled: "لغو شده",
      no_answer: "بی‌پاسخ",
      created: "ایجاد شده",
      blocked: "مسدود شده",
      sending: "در حال ارسال",
      answered: "پاسخ داده‌شده",
      device_off: "دستگاه خاموش",
      unreachable: "غیرقابل دسترس",
      invalid_number: "شماره نامعتبر",
      waiting_for_send: "در انتظار ارسال",
      partner_not_responding: "پاسخ از سمت شریک دریافت نشد",
    };

    const colors = {
      busy: "#E67E22",
      failed: "#E74C3C",
      created: "#2980B9",
      sending: "#F39C12",
      blocked: "#8E44AD",
      answered: "#27AE60",
      canceled: "#34495E",
      rejected: "#C0392B",
      no_answer: "#F1C40F",
      device_off: "#16A085",
      unreachable: "#95A5A6",
      invalid_number: "#9B59B6",
      waiting_for_send: "#7F8C8D",
      partner_not_responding: "#D35400",
    };

    return Object.keys(s).map((key) => ({
      key,
      valueDisplay: " تماس ",
      value: s[key as keyof typeof s] as number,
      name: labels[key as keyof typeof labels] || key,
      color: colors[key as keyof typeof colors] || "#BDC3C7",
    }));
  };
    
  return (
    <Stack position="relative" overflow={"hidden"}>
      <Card
        component={Stack}
        sx={{
          boxShadow: "none",
          borderRadius: "8px",
          border: "1px solid #ccc",
          backgroundColor: getCardColor(),
        }}
      >
        {waiting && <MuiLinearProgress />}
        <Stack
          p={2}
          direction="row"
          alignItems={"start"}
          justifyContent="space-between"
        >
          <Stack>
            <Typography>گزارش پیام‌های صوتی</Typography>
            <Typography variant="subtitle1">
              آخرین ریست :
              {formatTimestampWithTime({
                lang: "fa",
                timestamp: report?.data.last_reset_at,
              })}
            </Typography>
          </Stack>

          <Stack direction="row" gap={2} alignItems="center">
            {can(AccessEnums.AdminReportsVoicebroadcastsReset) && (
              <LoadingButton
                variant="contained"
                loading={resetWaiting}
                onClick={resetCallStatus}
              >
                ریست کردن پیام‌های صوتی
              </LoadingButton>
            )}

            <ReloadButton
              reloadAction={getData}
              forceRelode={forceReload}
              reloadCounter={refreshInterval > 0 ? refreshInterval : undefined}
            />
          </Stack>
        </Stack>

        {/* --- Status summary --- */}
        <Box
          sx={{
            p: 2,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            alignItems="center"
            flexWrap={"wrap"}
            gap={2}
          >
            <Stack direction="row" gap={0.5} alignItems="center">
              <Call color="#1976d2" variant="Bold" />
              <Typography variant="body2">
                کل تماس‌های برداشته‌شده:{" "}
                {report?.data.pickup?.toLocaleString() ?? 0}
              </Typography>
            </Stack>

            <Stack direction="row" gap={0.5} alignItems="center">
              <TickCircle color="#2e7d32" variant="Bold" />
              <Typography variant="body2">
                موفق: {report?.data.sent?.toLocaleString() ?? 0}
              </Typography>
            </Stack>

            <Stack direction="row" gap={0.5} alignItems="center">
              <CloseCircle color="#d32f2f" variant="Bold" />
              <Typography variant="body2">
                نامشخص: {report?.data.failed?.toLocaleString() ?? 0}
              </Typography>
            </Stack>

            <Stack direction="row" gap={0.5} alignItems="center">
              <Clock size="20" color="#f57c00" variant="Bold" />
              <Typography variant="body2">
                معلق:
                {(
                  (report?.data?.pickup ?? 0) -
                  (report?.data?.failed ?? 0) -
                  (report?.data?.sent ?? 0)
                ).toLocaleString()}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        {/* --- Donut chart --- */}
        <Stack justifyContent="start" alignItems="center" width="100%" p={2}>
          {report && report.data.pickup !== 0 ? (
            <DonutChart
              pieWrapSx={{
                width: "100%",
                alignItems: "center",
                flexDirection: "row-reverse",
                justifyContent: { xs: "start", md: "space-between" },
              }}
              data={getChartData(report)}
              legendSx={{ flexDirection: "column" }}
              //   loading={waiting}
            />
          ) : (
            <Typography sx={{ textAlign: "center", width: "100%", py: "30px" }}>
              {!waiting
                ? "دیتایی جهت نمایش وجود ندارد."
                : "در حال دریافت اطلاعات ..."}
            </Typography>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
